import { useCallback } from 'react';
import { ChakraProvider } from '@chakra-ui/react';
import { FormikHelpers } from 'formik';
import Loading from '@/components/general/Loading';
import { useToastContext } from '@/contexts/toastContext';
import useApiFetch from '@/hooks/useApiFetch';
import useApiSubmit from '@/hooks/useApiSubmit';
import useCandidateStatuses from '@/hooks/useCandidateStatuses';
import useProfile from '@/hooks/useProfileV2';
import candidatesServices, { CandidateToSubmit, CandidateResponse } from '@/services/candidates';
import { chakraExtendedTheme } from '@/styles/theme';
import { TechStackType, FetchApiIds } from '@/types';
import { scrollToFieldWithError } from '@/utils/formsValidation';
import NewCandidateComponent, { ValuesType, NewCandidateType } from './NewCandidate.component';

export type NewCandidateForm = Pick<
  NewCandidateType,
  'onHideCandidateFormClick' | 'isFormDisabled' | 'onDirtyForm'
> & {
  onAddNewCandidate: () => void;
};

const NewCandidateForm = function ({
  onAddNewCandidate,
  onHideCandidateFormClick,
  isFormDisabled,
  onDirtyForm,
  ...props
}: NewCandidateForm): JSX.Element {
  const toast = useToastContext();
  const { profile } = useProfile();
  const candidateStatuses = useCandidateStatuses();

  const createCandidateMutation = useApiSubmit<CandidateResponse, CandidateToSubmit>(
    candidatesServices.createCandidate
  );

  const allTechStacksResponse = useApiFetch<TechStackType[], void>(
    FetchApiIds.TECH_STACKS,
    candidatesServices.getAllTechsStacks
  );

  const onSubmitForm = async (
    { englishLevel, email, ...values }: ValuesType,
    actions: FormikHelpers<any>
  ): Promise<void> => {
    try {
      const formattedValues = {
        ...values,
        ...(englishLevel ? { englishLevel } : {}),
        ...(email ? { email } : {}),
      };

      await createCandidateMutation.mutateAsync({
        ...formattedValues,
        firstName: values.firstName.trim(),
        lastName: values.lastName.trim(),
        githubUrl: values?.githubUrl || null,
        isTalentPool: values?.isTalentPool === 'Yes',
        country: values?.country || null,
        developerType: values?.developerType || null,
        ownerId: profile.id || '',
        hasFintechExperience: values?.hasFintechExperience === 'Yes',
      });

      toast.setMessage('The candidate has been created.');
      toast.setSeverity('success');
      toast.showToast();
      actions.resetForm();
      onHideCandidateFormClick();
      onAddNewCandidate();
    } catch (err) {
      const defaultErrorMessage = 'There was a problem, please, try later.';
      const errorMessage = createCandidateMutation?.error?.response?.data?.message;
      toast.setMessage(errorMessage || defaultErrorMessage);
      toast.setSeverity('error');
      toast.showToast();
    }
  };

  const getUserInitials = useCallback((values) => {
    const { firstName, lastName } = values;

    const firstNameInitial = firstName?.[0] || 'N';
    const lastNameInitial = lastName?.[0] || 'C';

    return `${firstNameInitial}${lastNameInitial}`;
  }, []);

  const getUserName = useCallback((values) => {
    const { firstName, lastName } = values;

    if (lastName && firstName) {
      return `${firstName} ${lastName}`;
    }

    if (!firstName && lastName) {
      return lastName;
    }

    return firstName ?? 'New Candidate';
  }, []);

  if (allTechStacksResponse.isLoading) {
    return <Loading fullPage />;
  }

  return (
    <ChakraProvider theme={chakraExtendedTheme}>
      <NewCandidateComponent
        candidateStatuses={candidateStatuses}
        scrollToFieldWithError={() => scrollToFieldWithError('new-candidate-form-area')}
        onDirtyForm={onDirtyForm}
        isFormDisabled={isFormDisabled}
        onHideCandidateFormClick={onHideCandidateFormClick}
        onSubmitForm={onSubmitForm}
        getUserName={getUserName}
        getUserInitials={getUserInitials}
        {...props}
      />
    </ChakraProvider>
  );
};

export default NewCandidateForm;
